import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, EMPTY, from, switchMap, throwError } from 'rxjs';

import { AuthService } from '@auth/auth.service';
import { EnvironmentService } from '@core/services/environment.service';
import { HttpErrorStateService } from '@core/services/http-error-state.service';
import { AuthSessionCheckService } from '@core/services/auth-session-check.service';

export const GlobalHttpErrorInterceptor: HttpInterceptorFn = (req, next) => {
	const router = inject(Router);
	const injector = inject(Injector);
	const oauthService = inject(OAuthService);
	const environmentService = inject(EnvironmentService);
	const errorState = inject(HttpErrorStateService);
	const authSessionCheckService = inject(AuthSessionCheckService);

	const token = oauthService.getAccessToken();
	const isApiRequest = req.url.startsWith(environmentService.apiUrl);

	return next(req).pipe(
		catchError((error: HttpErrorResponse) => {
			console.log('[HTTP ERROR]', {
				url: req.url,
				status: error.status,
				isApiRequest,
				fatalErrorActive: errorState.isFatalErrorActive(),
				currentRoute: router.url,
			});

			// 1) Visible backend 5xx -> technical error page
			if (isApiRequest && error.status >= 500 && error.status < 600) {
				console.error('-> handling visible 5xx, redirecting to /500');

				if (!errorState.isFatalErrorActive()) {
					errorState.activateFatalError();
					void router.navigate(['/500'], { skipLocationChange: true });
				}

				// Consume the error so downstream code does not trigger logout or other flows
				return EMPTY;
			}

			// 2) Visible 401 -> logout
			if (isApiRequest && error.status === 401) {
				console.warn('-> handling visible 401, forcing logout');

				if (errorState.isFatalErrorActive()) {
					console.warn('-> skipping logout because fatal error page already won');
					return EMPTY;
				}

				if (token) {
					errorState.scheduleLogout(() => {
						if (errorState.isFatalErrorActive()) {
							errorState.stopLogout();
							return;
						}

						const authService = injector.get(AuthService);
						authService.forceLogout();

						setTimeout(() => {
							errorState.stopLogout();
						}, 3000);
					}, 0);
				}

				return EMPTY;
			}

			// 3) Ambiguous status 0 -> use userinfo as tie-breaker
			if (isApiRequest && error.status === 0) {
				console.warn('-> handling ambiguous status 0, checking userinfo');

				return from(authSessionCheckService.isStillAuthenticated()).pipe(
					switchMap((isAuthenticated) => {
						console.log('-> userinfo check result:', isAuthenticated);

						// If userinfo works, auth is likely still valid -> treat original error as technical
						if (isAuthenticated) {
							console.warn('-> userinfo says authenticated, treating status 0 as technical failure -> /500');

							if (!errorState.isFatalErrorActive()) {
								errorState.activateFatalError();
								void router.navigate(['/500'], { skipLocationChange: true });
							}

							return EMPTY;
						}

						// If userinfo says not authenticated -> logout
						console.warn('-> userinfo says NOT authenticated, treating status 0 as auth failure -> logout');

						if (errorState.isFatalErrorActive()) {
							console.warn('-> skipping logout because fatal error page already won');
							return EMPTY;
						}

						if (token) {
							errorState.scheduleLogout(() => {
								if (errorState.isFatalErrorActive()) {
									errorState.stopLogout();
									return;
								}

								const authService = injector.get(AuthService);
								authService.forceLogout();

								setTimeout(() => {
									errorState.stopLogout();
								}, 3000);
							}, 0);
						}

						return EMPTY;
					}),
					catchError((userinfoError) => {
						console.error('-> userinfo check failed, treating status 0 as technical failure -> /500', userinfoError);

						// If the tie-breaker itself is inconclusive, prefer technical error over false logout
						if (!errorState.isFatalErrorActive()) {
							errorState.activateFatalError();
							void router.navigate(['/500'], { skipLocationChange: true });
						}

						return EMPTY;
					}),
				);
			}

			// 4) Any other error -> let it propagate
			return throwError(() => error);
		}),
	);
};
